import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import axios from 'axios';
import { API } from '@/App';
import {
  CheckCircle2, AlertTriangle, ArrowRight, ChevronRight,
  FileText, FlaskConical, Pill, Calendar, ClipboardList,
  BookOpen, TestTube, History, Lightbulb, StickyNote, Shield
} from 'lucide-react';

const STEP_ICONS = {
  account: Shield,
  intake: FileText,
  documents: ClipboardList,
  decode: FlaskConical,
  phase: Calendar,
  protocol: ClipboardList,
  labs: TestTube,
  knowledge: BookOpen,
};

const STEP_COLORS = {
  account: '#7BA3C8',
  intake: '#C89B7B',
  documents: '#9B7BC8',
  decode: '#E07B7B',
  phase: '#4E7A6B',
  protocol: '#C8A87B',
  labs: '#7BADC8',
  knowledge: '#C87BAD',
};

export default function GoLiveChecklist({ open, onClose, onProceed, subscriberId, subscriberName }) {
  const [checklist, setChecklist] = useState([]);
  const [steps, setSteps] = useState([]);
  const [checked, setChecked] = useState({});
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const [defRes, savedRes] = await Promise.all([
          axios.get(`${API}/onboarding/checklist-definition`),
          subscriberId ? axios.get(`${API}/onboarding/${subscriberId}/checklist`) : Promise.resolve({ data: { checked_items: [] } }),
        ]);
        setChecklist(defRes.data.items || []);
        setSteps(defRes.data.steps || []);
        const saved = savedRes.data?.checked_items || [];
        const map = {};
        saved.forEach(k => { map[k] = true; });
        setChecked(map);
        setNotes(savedRes.data?.notes || '');
        setLoaded(true);
      } catch (err) {
        toast.error('Failed to load checklist');
      }
    })();
  }, [open, subscriberId]);

  const toggleItem = (key) => {
    setChecked(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const requiredItems = checklist.filter(i => i.required);
  const optionalItems = checklist.filter(i => !i.required);
  const requiredChecked = requiredItems.filter(i => checked[i.key]).length;
  const allRequiredDone = requiredChecked === requiredItems.length;
  const totalChecked = Object.values(checked).filter(Boolean).length;

  const handleSave = async (proceedAnyway = false) => {
    setSaving(true);
    try {
      if (subscriberId) {
        await axios.post(`${API}/onboarding/${subscriberId}/checklist`, {
          checked_items: Object.keys(checked).filter(k => checked[k]),
          notes,
          proceed_anyway: proceedAnyway,
        });
      }
      if (proceedAnyway && !allRequiredDone) {
        toast.warning('Proceeding with incomplete checklist — logged as warning');
      } else {
        toast.success('Checklist saved');
      }
      onProceed(proceedAnyway);
    } catch (err) {
      toast.error('Failed to save checklist');
    } finally {
      setSaving(false);
    }
  };

  if (!loaded && open) {
    return (
      <Dialog open={open} onOpenChange={onClose}>
        <DialogContent className="bg-adm-surface border-white/[0.06] max-w-2xl max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-center py-20">
            <div className="w-8 h-8 border-2 border-adm-accent border-t-transparent rounded-full animate-spin" />
          </div>
        </DialogContent>
      </Dialog>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-adm-surface border-white/[0.06] max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="go-live-checklist-modal">
        <DialogHeader>
          <DialogTitle className="text-adm-primary text-lg font-['Work_Sans']">
            Subscriber Go-Live Readiness
          </DialogTitle>
          {subscriberName && (
            <p className="text-adm-accent text-sm mt-0.5">{subscriberName}</p>
          )}
        </DialogHeader>

        {/* Section A: Mandatory Documents Checklist */}
        <div className="space-y-4 mt-3">
          <div>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-adm-primary text-sm font-medium font-['Work_Sans']">Mandatory Documents</h3>
              <Badge variant="outline" className={`text-[10px] ${allRequiredDone ? 'border-emerald-500/30 text-emerald-400 bg-emerald-500/8' : 'border-amber-500/30 text-amber-400 bg-amber-500/8'}`}>
                {requiredChecked}/{requiredItems.length} required
              </Badge>
            </div>
            <div className="space-y-1.5">
              {requiredItems.map(item => (
                <label key={item.key} className="flex items-center gap-3 p-2.5 rounded-lg bg-adm-bg/50 hover:bg-adm-bg/80 cursor-pointer transition-colors" data-testid={`checklist-${item.key}`}>
                  <Checkbox checked={!!checked[item.key]} onCheckedChange={() => toggleItem(item.key)} />
                  <span className={`text-xs flex-1 ${checked[item.key] ? 'text-adm-primary' : 'text-adm-secondary'}`}>{item.label}</span>
                  {checked[item.key] && <CheckCircle2 className="w-3.5 h-3.5 text-emerald-400" />}
                </label>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-adm-secondary text-xs font-medium mb-2 font-['Work_Sans']">Optional Documents</h4>
            <div className="space-y-1.5">
              {optionalItems.map(item => (
                <label key={item.key} className="flex items-center gap-3 p-2.5 rounded-lg bg-adm-bg/30 hover:bg-adm-bg/50 cursor-pointer transition-colors" data-testid={`checklist-${item.key}`}>
                  <Checkbox checked={!!checked[item.key]} onCheckedChange={() => toggleItem(item.key)} />
                  <span className={`text-xs flex-1 ${checked[item.key] ? 'text-adm-primary' : 'text-adm-tertiary'}`}>{item.label}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Section B: Go-Live Priority Order */}
          <div>
            <h3 className="text-adm-primary text-sm font-medium font-['Work_Sans'] mb-2">Go-Live Priority Order</h3>
            <div className="flex items-center gap-1.5 overflow-x-auto pb-2">
              {steps.map((step, i) => {
                const Icon = STEP_ICONS[step.key] || FileText;
                const color = STEP_COLORS[step.key];
                return (
                  <React.Fragment key={step.key}>
                    <div className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-md bg-adm-bg/50 flex-shrink-0" style={{ borderLeft: `2px solid ${color}` }}>
                      <Icon className="w-3 h-3" style={{ color }} />
                      <span className="text-[10px] text-adm-secondary whitespace-nowrap">{step.label}</span>
                    </div>
                    {i < steps.length - 1 && <ChevronRight className="w-3 h-3 text-adm-tertiary flex-shrink-0" />}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Section C: System State Message */}
          <div className="bg-amber-500/5 border border-amber-500/15 rounded-lg p-3" data-testid="system-state-message">
            <div className="flex items-start gap-2">
              <AlertTriangle className="w-4 h-4 text-amber-400 flex-shrink-0 mt-0.5" />
              <p className="text-xs text-amber-200/80 leading-relaxed">
                The system works best when onboarding is complete and structured.
                Incomplete onboarding will result in weak insights and poor daily experience.
              </p>
            </div>
          </div>

          {/* Notes */}
          <div>
            <label className="text-adm-secondary text-xs font-medium mb-1.5 block">Onboarding Notes</label>
            <Textarea
              value={notes} onChange={e => setNotes(e.target.value)}
              className="bg-adm-bg border-white/10 text-adm-primary text-xs resize-none" rows={2}
              placeholder="Any preparation notes..."
              data-testid="checklist-notes"
            />
          </div>
        </div>

        <DialogFooter className="flex-col sm:flex-row gap-2 mt-4">
          <Button variant="outline" onClick={onClose} className="border-white/10 text-adm-secondary hover:text-adm-primary text-xs">
            Save as Draft / Exit
          </Button>
          {!allRequiredDone && (
            <Button variant="outline" onClick={() => handleSave(true)} disabled={saving}
              className="border-amber-500/20 text-amber-400 hover:bg-amber-500/10 text-xs" data-testid="proceed-anyway-btn">
              <AlertTriangle className="w-3 h-3 mr-1.5" /> Proceed Anyway
            </Button>
          )}
          <Button onClick={() => handleSave(false)} disabled={saving || (!allRequiredDone)}
            className="bg-emerald-600/20 text-emerald-400 hover:bg-emerald-600/30 border-0 text-xs" data-testid="start-setup-btn">
            {saving ? <div className="w-3 h-3 border-2 border-emerald-400 border-t-transparent rounded-full animate-spin mr-1.5" /> : <ArrowRight className="w-3 h-3 mr-1.5" />}
            Start Subscriber Setup
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
